import { useState } from "react";

import { Button } from "common/components/ui/Button";
import { CountSpinner } from "common/components/ui/CountSpinner";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import WorkspaceImage from "common/components/WorkspaceImage";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import usePromotions from "common/datahooks/usePromotions";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";
import type { Workspace } from "common/types";

import AgencyLicenseSummary from "../AgencyLicenseSummary";

interface ManageSeatsDrawerProps {
  workspace: Workspace;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export default function ManageSeatsDrawer({
  workspace,
  isOpen,
  setIsOpen,
}: ManageSeatsDrawerProps) {
  const { id, agency_license_count, name, icon_url } = workspace;

  const [seatCount, setSeatCount] = useState(agency_license_count);

  const {
    agencyPlan: { unassigned_license_count },
  } = usePromotions();
  const { setLifetimeLicenseCount, isSettingLifetimeLicenseCount } =
    useLicenseMutations();

  const { agencyLicensedAccounts } = useLicensedAccounts(id);

  async function onSubmit() {
    if (seatCount !== agency_license_count) {
      await setLifetimeLicenseCount({
        workspaceId: id,
        count: seatCount,
        source: "agency",
        accountsToCancel: [],
      });
    }
    setIsOpen(false);
  }

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent>
        <DrawerHeader title="Manage seats" />
        <DrawerBody>
          <AgencyLicenseSummary isCompact />
          <div className="border rounded-3xl bg-purple-50/30 border-purple-50 my-4">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr className="text-purple-500 text-xs text-left">
                  <th className="px-3 py-2.5">Name</th>
                  <th className="px-3 py-2.5">Accounts / Seats</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-3 border-l rounded-l-3xl border-y bg-white border-black-200">
                    <div className="gap-x-2 flex items-center">
                      <WorkspaceImage id={id} imageSrc={icon_url} />
                      <span className="text-sm-bold">{name}</span>
                    </div>
                  </td>
                  <td className="p-3 border-r rounded-r-3xl border-y bg-white border-black-200">
                    <span className="text-button-xs ml-8">
                      {agencyLicensedAccounts.length}{" "}
                      <span className="text-black-400">
                        / {agency_license_count}
                      </span>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <CountSpinner
            variant="lg"
            value={seatCount}
            setValue={setSeatCount}
            maxValue={agency_license_count + unassigned_license_count}
          />
        </DrawerBody>
        <DrawerFooter className="justify-end">
          <DrawerClose asChild disabled={isSettingLifetimeLicenseCount}>
            <Button variant="secondary-black">Cancel</Button>
          </DrawerClose>
          <Button onClick={onSubmit} isLoading={isSettingLifetimeLicenseCount}>
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
